import React, { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Tabs,
  Spin,
  Card,
  Row,
  Col,
  message,
} from 'antd';
import {
  DownloadOutlined,
  SendOutlined,
  EyeOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import useIsMobile from 'hooks/useIsMobile';
import { downloadReceipt, sendReceipt } from 'store/slices/PaymentSlice';
import * as actions from 'store/actions/AuthActions';
import EmailInput from 'components/Inputs/EmailInput';
import { useNotification } from 'NotificationContext';
import axios from 'axios';

const SendReceiptDrawer = ({ open, onClose, payment }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const notify = useNotification();
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab] = useState('preview');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [, setSelectedTemplate] = useState(null);
  const [messagePreview, setMessagePreview] = useState(null);

  // Get default email from account data
  const getDefaultEmail = () => {
    if (payment?.account_data?.email) {
      return [payment.account_data.email];
    }
    if (payment?.invoice_data?.account_email) {
      return [payment.invoice_data.account_email];
    }
    return [];
  };

  // Load email templates
  const loadTemplates = async () => {
    try {
      const response = await actions.loadTemplatesWithType('PAYMENT_RECEIPT__');
      if (response.status === 200) {
        setTemplates(response?.data?.results || []);
      }
    } catch (e) {
      console.error('Error loading templates:', e);
    }
  };

  // Render template preview with actual data from backend
  const renderTemplatePreview = async (template) => {
    if (!template || !payment?.id) return;
    try {
      const response = await actions.paymentReceiptRender(payment.id, template.name);
      if (response?.status === 200) {
        setMessagePreview(response.data);
      }
    } catch (e) {
      console.error('Error rendering template:', e);
      setMessagePreview({
        message: template.html_content || template.content || 'Template preview not available',
      });
    }
  };

  // Handle template change
  const handleTemplateChange = (value) => {
    const template = templates.find((item) => item.id === value);
    setSelectedTemplate(template);
    if (template) {
      renderTemplatePreview(template);
    } else {
      setMessagePreview(null);
    }
  };

  // Handle download
  const handleDownload = async () => {
    if (!payment?.id) return;
    setDownloading(true);
    try {
      await dispatch(downloadReceipt({ id: payment.id, receiptNo: payment.receipt_no })).unwrap();
      message.success('Receipt downloaded!');
    } catch (error) {
      message.error('Failed to download receipt');
    } finally {
      setDownloading(false);
    }
  };

  // Handle send email
  const handleSendEmail = async (values) => {
    if (!payment?.id) return;
    setSending(true);
    try {
      await dispatch(
        sendReceipt({
          id: payment.id,
          to: values.to?.join(',') || '',
          cc: values.cc?.join(',') || '',
          bcc: values.bcc?.join(',') || '',
          subject: values.subject,
          template: values.template,
        })
      ).unwrap();
      notify('success', 'Success', 'Receipt sent successfully!');
      form.resetFields();
      onClose();
    } catch (error) {
      notify('error', 'Error', `Failed to send receipt. ${error}`);
    } finally {
      setSending(false);
    }
  };

  // Fetch PDF when drawer opens
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchReceiptPdf = async () => {
      if (open && payment?.id) {
        setPdfLoading(true);
        try {
          const response = await axios.get(
            `invoices/payments/${payment.id}/receiptView/`,
            { responseType: 'blob' }
          );
          const blobUrl = URL.createObjectURL(response.data);
          setPdfUrl(blobUrl);
        } catch (error) {
          console.error('Error loading receipt PDF:', error);
          message.error('Failed to load receipt preview');
        } finally {
          setPdfLoading(false);
        }
      } else {
        if (pdfUrl) {
          URL.revokeObjectURL(pdfUrl);
        }
        setPdfUrl('');
      }
    };
    fetchReceiptPdf();
  }, [open, payment?.id]);

  // Reset state when drawer opens/closes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && payment?.id) {
      loadTemplates();
      setActiveTab('preview');
      setMessagePreview(null);
      setSelectedTemplate(null);

      // Set default email
      const defaultEmail = getDefaultEmail();
      form.setFieldsValue({
        to: defaultEmail,
        subject: `Payment Receipt ${receiptDisplay} - Thank You for Your Payment`,
      });
    }
  }, [open, payment]);

  // Get receipt display ID
  const receiptDisplay =
    payment?.receipt_no ||
    payment?.transaction_id ||
    payment?.transaction_ref ||
    `RCP-${payment?.id?.substring(0, 8)}`;

  const tabItems = [
    {
      key: 'preview',
      label: (
        <span>
          <EyeOutlined style={{ marginRight: 8 }} />
          Preview
        </span>
      ),
      children: (
        <div style={{ height: 'calc(100vh - 200px)' }}>
          {pdfLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Spin size="large" tip="Loading receipt..." />
            </div>
          ) : pdfUrl ? (
            <iframe
              src={`${pdfUrl}#view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
              title="Payment Receipt PDF"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: '#999',
              }}
            >
              No receipt available
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'send',
      label: (
        <span>
          <MailOutlined style={{ marginRight: 8 }} />
          Send Email
        </span>
      ),
      children: (
        <div style={{ padding: '16px 0' }}>
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            onFinish={handleSendEmail}
            initialValues={{
              subject: `Payment Receipt ${receiptDisplay} - Thank You for Your Payment`,
            }}
          >
            <EmailInput
              label="To"
              name="to"
              required
              message="Please enter at least one email address"
              defaultValue={getDefaultEmail()}
            />
            <EmailInput label="CC" name="cc" />
            <EmailInput label="BCC" name="bcc" />

            <Form.Item
              label="Template"
              name="template"
              rules={[{ required: true, message: 'Please select a template!' }]}
            >
              <Select
                placeholder="Select Email Template"
                allowClear
                onChange={handleTemplateChange}
                options={templates.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Subject"
              name="subject"
              rules={[{ required: true, message: 'Please enter a subject!' }]}
            >
              <Input placeholder="Email subject" />
            </Form.Item>

            {messagePreview && (
              <Row style={{ marginBottom: 16 }}>
                <Col span={24} offset={0}>
                  <Card
                    title="Message Preview"
                    size="small"
                    style={{ backgroundColor: '#f9f9f9' }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: messagePreview?.message || '',
                      }}
                      style={{ maxHeight: 300, overflow: 'auto' }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            <Form.Item wrapperCol={{ offset: 4 }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                loading={sending}
              >
                {sending ? 'Sending...' : 'Send Receipt'}
              </Button>
            </Form.Item>
          </Form>
        </div>
      ),
    },
  ];

  return (
    <Drawer
      title={`Payment Receipt - ${receiptDisplay}`}
      placement={isMobile ? "bottom" : "right"}
      onClose={onClose}
      open={open}
      width={isMobile ? "100%" : "50%"}
      height={isMobile ? "90vh" : undefined}
      styles={{
        body: { paddingBottom: isMobile ? 80 : 24 }
      }}
      extra={
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          loading={downloading}
          size={isMobile ? "small" : "middle"}
        >
          {isMobile ? "Download" : "Download PDF"}
        </Button>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ height: '100%' }}
      />
    </Drawer>
  );
};

export default SendReceiptDrawer;
